import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { velaGuide, queueConfirmFix } from '@/lib/velaGuide';

export const OAUTH_POPUP_NAME = 'veluro-oauth';
const RESULT_KEYS = ['meta_connected', 'meta_error', 'google_connected', 'google_error', 'google_ads_connected', 'google_ads_error'];

/* Runs on every route: when we are the OAuth popup landing back on our own origin, tell the opener and close. */
export function OAuthPopupBridge() {
  useEffect(() => {
    if (!window.opener || window.name !== OAUTH_POPUP_NAME) return;
    const params = new URLSearchParams(window.location.search);
    const hit = RESULT_KEYS.find((k) => params.has(k));
    if (!hit) return;
    window.opener.postMessage({ type: 'veluro-oauth', ok: hit.endsWith('_connected'), error: hit.endsWith('_error') ? params.get(hit) : null }, window.location.origin);
    window.close();
  }, []);
  return null;
}

/* Opens the provider consent screen in a popup; resolves {ok, error} when the popup reports back or closes. */
export function openOAuthPopup(startPath) {
  const w = 600, h = 740;
  const left = Math.max(0, (window.screen.width - w) / 2), top = Math.max(0, (window.screen.height - h) / 2);
  const popup = window.open('', OAUTH_POPUP_NAME, `width=${w},height=${h},left=${left},top=${top},popup=1`);
  return new Promise((resolve) => {
    let settled = false;
    const done = (r) => { if (settled) return; settled = true; window.removeEventListener('message', onMsg); clearInterval(timer); resolve(r); };
    const onMsg = (e) => { if (e.origin === window.location.origin && e.data?.type === 'veluro-oauth') done({ ok: !!e.data.ok, error: e.data.error }); };
    window.addEventListener('message', onMsg);
    const timer = setInterval(() => { if (!popup || popup.closed) done({ ok: null }); }, 600);
    api.get(startPath).then((r) => {
      if (!popup) { window.location.href = r.data.authorization_url; return; }
      popup.location.href = r.data.authorization_url;
    }).catch((e) => { popup?.close(); done({ ok: false, error: e.response?.data?.detail || 'Could not start sign-in' }); });
  });
}

const TONE = {
  expired: 'border-rose-500/30 bg-rose-500/[0.08] text-rose-100',
  expiring_soon: 'border-amber-500/30 bg-amber-500/[0.08] text-amber-100',
};

/* Banner for Social / Campaigns: shows when a Facebook or Google connection is expired (or about to), with one-click reconnect. */
export function ConnectionHealthBanner({ services = ['meta_ads', 'google_ads'], className = '' }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');
  const [dismissed, setDismissed] = useState(() => new Set());
  const key = services.join(',');
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/integrations/health?services=${key}`);
      if (!mounted.current) return;
      const att = r.data.attention || [];
      setItems(att);
      att.filter((c) => c.status === 'expired').forEach((c) => velaGuide({ source: 'this page', platform: c.service,
        problems: [`Your ${c.label} connection has expired${c.account ? ` (${c.account})` : ''}. ${c.detail || `Reconnect to keep ${c.used_for} running.`}`] }));
    } catch {}
  }, [key]);
  useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false; }; }, [load]);

  const reconnect = async (c) => {
    setBusy(c.service);
    const res = await openOAuthPopup(c.reconnect);
    if (res.ok === false && res.error) { toast.error(res.error); setBusy(''); return; }
    const r = await api.get(`/integrations/health?services=${c.service}`).catch(() => null);
    const now = r?.data?.connections?.[0];
    if (now && now.status === 'healthy') {
      toast.success(`${c.label} reconnected — refreshing…`);
      queueConfirmFix(c.service, 'reconnected'); // after the reload Vela re-runs the ad-account checks and confirms the fix
      setTimeout(() => window.location.reload(), 700);
      return;
    }
    if (res.ok === null) toast.message('Sign-in window closed before finishing — nothing changed.');
    setBusy('');
    load();
  };

  const visible = items.filter((c) => !dismissed.has(c.service));
  if (!visible.length) return null;
  return (
    <div className={`space-y-2 ${className}`} data-testid="connection-health-banners">
      {visible.map((c) => (
        <div key={c.service} role="alert" data-testid={`connection-banner-${c.service}`} data-status={c.status}
          className={`rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap ${TONE[c.status] || TONE.expired}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">
              {c.status === 'expired' ? `Your ${c.label} connection has expired` : `Your ${c.label} connection expires soon`}
              {c.account ? <span className="font-normal opacity-70"> · {c.account}</span> : null}
            </p>
            <p className="text-[12px] opacity-80 leading-snug">{c.detail || `Reconnect to keep ${c.used_for} running.`}</p>
          </div>
          <button type="button" onClick={() => reconnect(c)} disabled={!!busy} data-testid={`connection-reconnect-${c.service}`}
            className={`h-9 px-3.5 rounded-lg text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 ${c.status === 'expired' ? 'bg-rose-500 hover:bg-rose-400 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'}`}>
            {busy === c.service ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Reconnect {c.provider === 'facebook' ? 'Facebook' : 'Google'}
          </button>
          {c.status === 'expiring_soon' && (
            <button type="button" onClick={() => setDismissed((s) => new Set([...s, c.service]))} aria-label="Dismiss" data-testid={`connection-dismiss-${c.service}`}
              className="h-8 w-8 rounded-full flex items-center justify-center opacity-60 hover:opacity-100 hover:bg-white/10"><X className="h-4 w-4" /></button>
          )}
        </div>
      ))}
    </div>
  );
}

export default ConnectionHealthBanner;
